import { startCase } from "lodash";
import { t } from "@/i18n-js/instance";
import { Typography } from "@circle-react-shared/uikit/Typography";
import {
  formatValueDynamically,
  getFormattedValue,
} from "../../../../utils/previewValueFormatterUtils";
import { PreviewCardLayout } from "./PreviewCardLayout";

type ProfileFieldParam = string | string[] | boolean;

interface ProfileFieldCardProps {
  params: Record<string, ProfileFieldParam>;
  url: string;
}

interface ProfileFieldConfig {
  defaultValue?: string | string[] | boolean;
  label: string;
  type: string;
}

export const PROFILE_FIELDS: Record<string, ProfileFieldConfig> = {
  label: {
    label: t("profile_fields.field_modal.label", { defaultValue: "Name" }),
    type: "string",
  },
  key: {
    label: t("profile_fields.field_modal.key", { defaultValue: "Key" }),
    type: "string",
  },
  placeholder: {
    label: t("profile_fields.field_modal.placeholder_name", {
      defaultValue: "Placeholder",
    }),
    type: "string",
  },
  field_type: {
    label: t("profile_fields.field_modal.field_type", {
      defaultValue: "Field type",
    }),
    type: "humanize",
  },
  hidden: {
    label: t("profile_fields.field_modal.hidden", {
      defaultValue: "Internal field",
    }),
    type: "boolean",
  },
  required: {
    label: t("profile_fields.field_modal.required", {
      defaultValue: "Mark this field as required",
    }),
    type: "boolean",
  },
  form_locations: {
    label: t("profile_fields.form_locations", {
      defaultValue: "Form locations",
    }),
    type: "string",
  },
  profile_view: {
    label: t("profile_fields.pages.profile_view.title", {
      defaultValue: "Visible in full profile view",
    }),
    type: "boolean",
  },
};

const HIDDEN_KEYS = ["signup", "edit_profile", "community_view"];

const formatFormLocations = (
  params: Record<string, string | string[] | boolean>,
) => {
  const locations: string[] = [];
  const signupEnabled = "signup" in params ? params.signup : null;
  const editEnabled = "edit_profile" in params ? params.edit_profile : null;

  if (signupEnabled === true) {
    locations.push(
      t("profile_fields.pages.signup.title", { defaultValue: "Signup form" }),
    );
  }
  if (editEnabled === true) {
    locations.push(
      t("profile_fields.pages.edit_profile.title", {
        defaultValue: "Edit profile form",
      }),
    );
  }

  if (locations.length === 0) return null;
  if (locations.length === 1) return locations[0];
  if (locations.length === 2) return `${locations[0]} and ${locations[1]}`;
  return locations.join(", ");
};

export const ProfileFieldCard = ({ params, url }: ProfileFieldCardProps) => {
  const formLocationsValue = formatFormLocations(params);
  const fullProfileVisible =
    "profile_view" in params ? params.profile_view : null;

  return (
    <PreviewCardLayout
      title={t(
        "help_widget.copilot.preview_cards.create_profile_field_card_title",
      )}
      url={url}
    >
      <ul className="flex flex-col leading-5">
        {Object.entries(PROFILE_FIELDS).flatMap(([key, keyProps]) => {
          let value: ProfileFieldParam | undefined = keyProps.defaultValue;

          if (key === "form_locations") {
            value = formLocationsValue ?? keyProps.defaultValue;
          } else if (key === "profile_view") {
            value =
              fullProfileVisible != null
                ? fullProfileVisible
                : keyProps.defaultValue;
          } else if (key in params && params[key] != null) {
            value = params[key];
          }

          if (value == null) return [];

          return [
            <li key={key} className="text-darkest !pb-0">
              <Typography.BodySm color="text-darkest">
                <strong>{keyProps.label}:</strong>{" "}
                {getFormattedValue(value, keyProps.type)}
              </Typography.BodySm>
            </li>,
          ];
        })}
        {Object.entries(params)
          .filter(
            ([key, value]) =>
              !(key in PROFILE_FIELDS) &&
              !HIDDEN_KEYS.includes(key) &&
              value != null,
          )
          .map(([key, value]) => (
            <li key={key} className="text-darkest !pb-0">
              <Typography.BodySm color="text-darkest">
                <strong>{startCase(key)}:</strong>{" "}
                {formatValueDynamically(value)}
              </Typography.BodySm>
            </li>
          ))}
      </ul>
    </PreviewCardLayout>
  );
};
